import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

import { useDispatch } from 'react-redux';
import { CoApplicantModal, getRelationship } from '@modules/ContactManager/components/Tabs/components/Contact/components/CoApplicant/CoApplicant';
import { SwapConfirmationModal } from '@modules/ContactManager/components/Tabs/shared/DealCalculator/components/SwapConfirmationModal';
import { formatDate } from '@shared/commonFunctions';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { colorTheme } from '@styles/muiTheme';
import { DealsButton } from '@styles/StyledButtons';
import { CoBorrowerControls, CoBorrowerSection, CoBorrowerWrapper, ControlGroup } from '@styles/StyledDiv';

import { SearchInput } from '@shared/inputs/SearchInput';
import { SERVICES } from '@shared/constants/apiConfig';
import { Loader } from '@shared/enhancers/withLoader';
import { setLastCommentsRefresh } from '@store/reducers/contactManagerSlice';
import { IContactData } from '../../interfaces';
import { handleEditCoBorrower } from '../../queries';
import { IReferralItemValue } from '../../../Contact/interfaces';
import { relationShipOptions } from './constants';
import { CoBorrowerProps, IName, RelationshipEnum } from './types';

const BorrowerInfo = ({ form }: { form: { label: string; value?: string | number }[] }) => (
  <CoBorrowerWrapper>
    {form?.map(({ label, value }, idx) => (
      <CoBorrowerSection key={idx}>
        <Typography
          fontWeight="bold"
          color={colorTheme.palette.common.black}
        >
          {label}
        </Typography>
        {Array.isArray(value) ? (
          value.map((item, index) => (
            <Typography
              key={index}
              whiteSpace="break-spaces"
            >
              {String(item)}
            </Typography>
          ))
        ) : (
          <Typography whiteSpace="break-spaces">{value || '—'}</Typography>
        )}
      </CoBorrowerSection>
    ))}
  </CoBorrowerWrapper>
);

const getContactDataForResponse = (id: string, relationshipType: RelationshipEnum) =>
  id ? { contactId: id, relationShipType: relationshipType } : null;

const commonFirstFields = (borrowerData: IContactData | null) => {
  const fullName = `${borrowerData?.firstName} ${borrowerData?.lastName}`;

  return [
    { label: 'Contact Id', value: borrowerData?.numericId },
    { label: 'Name', value: fullName },
  ];
};

const commonFields = (borrowerData: IContactData | null) => [
  { label: 'DL#', value: borrowerData?.driverLicenseNumber },
  { label: 'DL State', value: borrowerData?.driverLicenseState },
  { label: 'Issue Date', value: formatDate(borrowerData?.driverLicenseIssuesOnDate) },
  { label: 'Exp Date', value: formatDate(borrowerData?.driverLicenseExpireDate) },
  { label: 'Address', value: borrowerData?.addresses?.[0]?.streetLine1 },
];

export const CoBorrower = ({ contactId, dealData, reloadDeal, refreshDealsTable }: CoBorrowerProps) => {
  const dispatch = useDispatch();
  const { data: fetchedContactData } = useSWR<IContactData>(contactId && `/contact-service/contacts/${contactId}`);

  const [coApplicantModalId, setCoApplicantModalId] = useState('');

  const [coBorrowedRelationshipType, setCoBorrowedRelationshipType] = useState<RelationshipEnum>(RelationshipEnum.None);
  const [droppedCoBorrowedRelationshipType, setDroppedCoBorrowedRelationshipType] = useState<RelationshipEnum>(
    RelationshipEnum.None
  );

  const [selectedCoBorrowerId, selectCoBorrowerId] = useState('');
  const [selectedDroppedCoBorrowerId, selectDroppedCoBorrowerId] = useState('');
  const [coApplicants, setCoApplicants] = useState<IName[]>([]);
  const [coApplicantModalOpenStatus, setCoApplicantModalOpenStatus] = useState(0);

  const [swapAlertModal, setSwapAlertModal] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const {
    data: fetchedCoBorrowerData,
    mutate: mutateCoBorrowerData,
    isLoading: isCoBorrowerDataLoading,
  } = useSWR<IContactData>(selectedCoBorrowerId && `/contact-service/contacts/${selectedCoBorrowerId}`);

  const {
    data: fetchedDroppedCoBorrowerData,
    mutate: mutateDroppedCoBorrowerData,
    isLoading: isDroppedCoBorrowerDataLoading,
  } = useSWR<IContactData>(selectedDroppedCoBorrowerId && `/contact-service/contacts/${selectedDroppedCoBorrowerId}`);

  const isExistingCoApplicantId = useMemo(
    () => coApplicants.find((item) => item.contactId === coApplicantModalId),
    [coApplicants, coApplicantModalId]
  );

  useEffect(() => {
    selectCoBorrowerId(dealData?.coContact?.id ?? '');
    selectDroppedCoBorrowerId(dealData?.droppedCoContact?.id ?? '');
  }, [dealData?.coContact?.id, dealData?.droppedCoContact?.id]);

  const coborrowerForm = useMemo(
    () =>
      selectedCoBorrowerId && fetchedCoBorrowerData
        ? [
            ...commonFirstFields(fetchedCoBorrowerData),
            { label: 'DOB', value: formatDate(fetchedCoBorrowerData?.dateOfBirth) },
            { label: 'Home Phone', value: fetchedCoBorrowerData?.homePhone },
            { label: 'Cell Phone', value: fetchedCoBorrowerData?.cellPhone },
            ...commonFields(fetchedCoBorrowerData),
            { label: 'Income Summary', value: fetchedCoBorrowerData?.incomeSummary },
            { label: 'Employment', value: fetchedCoBorrowerData?.employmentSummary },
            { label: 'Expense Summary', value: fetchedCoBorrowerData?.expenseAmount },
            { label: 'Score', value: fetchedCoBorrowerData?.fico?.[0]?.fico },
          ]
        : [],
    [fetchedCoBorrowerData, selectedCoBorrowerId]
  );

  const droppedCoborrowerForm = useMemo(
    () =>
      selectedDroppedCoBorrowerId && fetchedDroppedCoBorrowerData
        ? [...commonFirstFields(fetchedDroppedCoBorrowerData), ...commonFields(fetchedDroppedCoBorrowerData)]
        : [],
    [fetchedDroppedCoBorrowerData, selectedDroppedCoBorrowerId]
  );

  useEffect(() => {
    setDroppedCoBorrowedRelationshipType(getRelationship(dealData?.droppedCoContact) as RelationshipEnum);
    setCoBorrowedRelationshipType(getRelationship(dealData?.coContact) as RelationshipEnum);
  }, [dealData?.droppedCoContact?.relationShip, dealData?.coContact?.relationShip]);

  useEffect(() => {
    if (fetchedContactData?.coApplicants && fetchedContactData?.coApplicants?.length > 0) {
      const mapped = fetchedContactData?.coApplicants.map((item) => ({
        contactId: item.id,
        firstName: item.firstName,
        lastName: item.lastName,
      }));
      setCoApplicants(mapped);
    }
  }, [fetchedContactData?.coApplicants]);

  const handleChangeCoBorrower = useCallback(
    async (value: string) => {
      await handleEditCoBorrower(dealData?.id || '', {
        coContact: getContactDataForResponse(value || '', coBorrowedRelationshipType),
        droppedCoContact: getContactDataForResponse(selectedDroppedCoBorrowerId || '', droppedCoBorrowedRelationshipType),
      }).then(() => {
        selectCoBorrowerId(value);
        setCoApplicantModalId(value || '');
        mutateCoBorrowerData();
        reloadDeal?.();
        refreshDealsTable?.();
        dispatch(setLastCommentsRefresh(new Date().toISOString()));
      });
    },
    [handleEditCoBorrower, coBorrowedRelationshipType, selectedDroppedCoBorrowerId, droppedCoBorrowedRelationshipType, dealData]
  );

  const handleChangeDroppedCoBorrower = useCallback(
    async (value: string) => {
      await handleEditCoBorrower(dealData?.id || '', {
        coContact: getContactDataForResponse(selectedCoBorrowerId || '', coBorrowedRelationshipType),
        droppedCoContact: getContactDataForResponse(value || '', droppedCoBorrowedRelationshipType),
      }).then(() => {
        selectDroppedCoBorrowerId(value);
        setCoApplicantModalId(value || '');
        mutateDroppedCoBorrowerData();
        reloadDeal?.();
        refreshDealsTable?.();
        dispatch(setLastCommentsRefresh(new Date().toISOString()));
      });
    },
    [handleEditCoBorrower, dealData, selectedCoBorrowerId, coBorrowedRelationshipType, droppedCoBorrowedRelationshipType]
  );

  const handleChangeCoBorrowerRelationShip = useCallback(
    (value: RelationshipEnum) => {
      if (selectedCoBorrowerId) {
        setCoBorrowedRelationshipType(value || RelationshipEnum.None);
        handleEditCoBorrower(dealData?.id || '', {
          coContact: getContactDataForResponse(selectedCoBorrowerId, value || RelationshipEnum.None),
          droppedCoContact: getContactDataForResponse(selectedDroppedCoBorrowerId || '', droppedCoBorrowedRelationshipType),
        });
      }
    },
    [selectedCoBorrowerId, handleEditCoBorrower, dealData, selectedDroppedCoBorrowerId, droppedCoBorrowedRelationshipType]
  );

  const handleChangeDroppedCoBorrowerRelationShip = useCallback(
    (value: RelationshipEnum) => {
      if (selectedDroppedCoBorrowerId) {
        setDroppedCoBorrowedRelationshipType(value || RelationshipEnum.None);
        handleEditCoBorrower(dealData?.id || '', {
          coContact: getContactDataForResponse(selectedCoBorrowerId || '', coBorrowedRelationshipType),
          droppedCoContact: getContactDataForResponse(selectedDroppedCoBorrowerId, value || RelationshipEnum.None),
        });
      }
    },
    [selectedCoBorrowerId, handleEditCoBorrower, dealData, selectedDroppedCoBorrowerId, coBorrowedRelationshipType]
  );

  const handleOnEditClick = useCallback(
    (id: string) => {
      setCoApplicantModalId(id);
      !!isExistingCoApplicantId && setCoApplicantModalOpenStatus(prevState => prevState + 1);
    },
    [isExistingCoApplicantId]
  );

  return (
    <div>
      <Typography
        variant="h3"
        color={colorTheme.palette.common.black}
        sx={{ mb: '12px' }}
      >
        Co-Borrower
      </Typography>

      <CoBorrowerControls>
        <ControlGroup>
          <SearchInput<IReferralItemValue, string>
            label="Related Co-Borrower"
            fetchUrl={`${SERVICES.CONTACT}/contacts/names`}
            defaultValue={{
              label: dealData?.coContact?.contactName ?? '',
              value: dealData?.coContact?.id ?? '',
            }}
            mapOptionsFn={(option) => ({
              label: `${option.firstName} ${option.lastName}`,
              value: option.contactId,
            })}
            params={{
              contactId: contactId,
            }}
            onChange={handleChangeCoBorrower}
          />

          <DealsButton
            disabled={!selectedCoBorrowerId || !isExistingCoApplicantId}
            onClick={() => selectedCoBorrowerId && handleOnEditClick(selectedCoBorrowerId)}
          >
            Edit
          </DealsButton>
        </ControlGroup>

        <ControlGroup>
          <SelectAutocomplete
            hideNoneOption
            label="Relationship"
            value={coBorrowedRelationshipType}
            options={relationShipOptions}
            onChange={(value: RelationshipEnum) => handleChangeCoBorrowerRelationShip(value)}
          />

          <DealsButton
            disabled={!selectedCoBorrowerId}
            onClick={() => setSwapAlertModal(true)}
          >
            Swap
          </DealsButton>
        </ControlGroup>
      </CoBorrowerControls>

      {isCoBorrowerDataLoading && <Loader minHeight="170px" />}

      {selectedCoBorrowerId && coborrowerForm.length > 0 && <BorrowerInfo form={coborrowerForm} />}

      <CoBorrowerControls>
        <ControlGroup>
          <SearchInput<IReferralItemValue, string>
            label="Dropped Co-Borrower"
            fetchUrl={`${SERVICES.CONTACT}/contacts/names`}
            defaultValue={{
              label: dealData?.droppedCoContact?.contactName ?? '',
              value: dealData?.droppedCoContact?.id ?? '',
            }}
            mapOptionsFn={(option) => ({
              label: `${option.firstName} ${option.lastName}`,
              value: option.contactId,
            })}
            params={{
              contactId: contactId,
              isDropped: true,
            }}
            onChange={handleChangeDroppedCoBorrower}
          />

          <DealsButton
            disabled={!selectedDroppedCoBorrowerId || !isExistingCoApplicantId}
            onClick={() => selectedDroppedCoBorrowerId && handleOnEditClick(selectedDroppedCoBorrowerId)}
          >
            Edit
          </DealsButton>
        </ControlGroup>

        <ControlGroup>
          <SelectAutocomplete
            hideNoneOption
            label="Relationship"
            value={droppedCoBorrowedRelationshipType}
            options={relationShipOptions}
            onChange={(value: RelationshipEnum) => handleChangeDroppedCoBorrowerRelationShip(value)}
          />
        </ControlGroup>
      </CoBorrowerControls>

      {isDroppedCoBorrowerDataLoading && <Loader minHeight="170px" />}

      {selectedDroppedCoBorrowerId && droppedCoborrowerForm.length > 0 && <BorrowerInfo form={droppedCoborrowerForm} />}

      {!!coApplicantModalOpenStatus && (
        <CoApplicantModal
          open
          coApplicantModalPosition={coApplicantModalOpenStatus}
          coApplicantIdToEdit={coApplicantModalId}
          contactId={contactId || ''}
          onClose={() => setCoApplicantModalOpenStatus(0)}
        />
      )}

      <LoaderBackdrop open={isLoading} />

      {swapAlertModal && dealData?.coContact && (
        <SwapConfirmationModal
          assetId={dealData?.assetId}
          leadId={dealData?.leadId}
          borrower={dealData.contact}
          coBorrower={dealData.coContact}
          dealId={dealData?.id}
          assetNumericId={dealData?.assetNumericId}
          numericId={dealData?.numericId}
          openSwapAlert={swapAlertModal}
          setOpenSwapAlert={setSwapAlertModal}
          setLoading={setLoading}
        />
      )}

      <Box height="25px" />
    </div>
  );
};
